import React, { CSSProperties } from 'react';
import { ILeaveType, IProjectOption } from 'types';

/**
 * Generates a label for a task by combining the WBS (Work Breakdown Structure) ID and the task title.
 *
 * @param wbs_id - The WBS ID to be included in the label.
 * @param title - The title of the task.
 * @returns A string that combines the WBS ID and the title, formatted as "(WBS_ID) Title" if the WBS ID is provided, otherwise just the title.
 */
export const getTaskLabel = (wbs_id: string, title: string) =>
  wbs_id ? `(${wbs_id}) ${title}` : title;

/**
 * Generates a label for an accounting code by concatenating the code, description, and archived status.
 *
 * @param accounting_code - The accounting code to be labeled.
 * @param description - A description of the accounting code.
 * @param archived - A boolean indicating if the accounting code is archived.
 * @returns A string representing the formatted label for the accounting code.
 */
export const getAccountingCodeLabel = (
  accounting_code: string,
  description: string,
  archived: boolean,
) =>
  accounting_code +
  (description ? ` - ${description}` : '') +
  (archived ? '(Archived)' : '');

/**
 * Resource
 */
export const getResourceLabel = (resourceCode: string, resourceName: string) =>
  resourceCode && resourceName ? `(${resourceCode}) ${resourceName}` : '';

/**
 * Cost Centre
 * @param code
 * @param name
 * @returns
 */
export const getCostCentreLabel = (
  code: string | undefined,
  name: string | undefined,
  archived?: boolean,
) => {
  const baseLabel = code && name ? `(${code}) ${name}` : name;
  return archived ? `${baseLabel} (Archived)` : baseLabel;
};

export const getProjectLabel = (jobId: string, name: string) =>
  jobId && name ? `(${jobId}) ${name}` : name;

export const getProjectLabelWithStatus = (option: IProjectOption) =>
  `(${option?.job_id}) ${option?.name} ${
    option?.status === 3 ? '(Completed)' : ''
  }`;

/**
 * Employee
 */
export const getEmployeeLabel = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`;

export const getEmployeeLabelWithStatus = ({
  firstName,
  lastName,
  id,
  isActive,
  style,
  returnString = false,
}: {
  firstName: string;
  lastName: string;
  id?: string;
  isActive?: boolean;
  style?: CSSProperties;
  returnString?: boolean;
}) => {
  const name = `${firstName} ${lastName}`;
  const idLabel = id ? `(${id})` : '';
  if (returnString) {
    return isActive ? `${name} ${idLabel}` : `${name} ${idLabel} (Archived)`;
  }
  return (
    <>
      {name} {idLabel} {!isActive && <span style={style}>(Archived)</span>}
    </>
  );
};

export const getEquipmentLabel = (
  equipmentName: string,
  equipmentId: string,
  reverseOrder = false,
) =>
  reverseOrder
    ? `(${equipmentId}) ${equipmentName}`
    : `${equipmentName} (${equipmentId})`;

/**
 * Generates a user label by combining the first name, last name, and optional role.
 *
 * @param firstName - The user's first name.
 * @param lastName - The user's last name.
 * @param role - (Optional) The user's role.
 * @returns A formatted string representing the user label.
 */
export const getUserLabel = (
  firstName: string,
  lastName: string,
  role?: string,
): string => {
  const roleLabel = role ? `(${role})` : '';
  return `${firstName} ${lastName} ${roleLabel}`.trim();
};

export const getUserLabelWithEmail = ({
  firstName,
  lastName,
  fullName,
  email,
}: {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
}) => {
  const name = fullName || `${firstName} ${lastName}`;
  const emailLabel = email ? `(${email})` : '';
  return `${name} ${emailLabel}`.trim();
};

/**
 * Generates a label for a leave type by combining the name or display name with archived status.
 *
 * @param name - The name of the leave type.
 * @param display_name - The display name of the leave type (optional).
 * @param is_archived - A boolean indicating if the leave type is archived.
 * @returns A string representing the formatted label for the leave type, with "(archived)" suffix if archived.
 */
export const getLeaveTypeOptionLabel = ({
  name,
  display_name,
  is_archived,
  style,
  asString = false,
}: Pick<ILeaveType, 'name' | 'display_name' | 'is_archived'> & {
  style?: CSSProperties;
  asString?: boolean;
}) => {
  const label = display_name || name;
  if (asString) {
    return is_archived ? `${label} (Archived)` : label;
  }
  return (
    <React.Fragment>
      {label} {is_archived ? <span style={style}>(Archived)</span> : null}
    </React.Fragment>
  );
};

export const getSupplierLabel = (supplierName: string, isArchived: boolean) =>
  isArchived ? `${supplierName} (Archived)` : supplierName;
